import { useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  CircularProgress,
  Snackbar,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Collapse,
  Divider,
} from "@mui/material";
import {
  Key as KeyIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Security as SecurityIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  CheckCircle as CheckIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  ExpandLess,
  ExpandMore,
} from "@mui/icons-material";
import { BrokerProvider } from "../../../data/brokers";
import { 
  useLinkAccount, 
  useDeleteAccount, 
  useTriggerSync,
  LinkedAccount 
} from "../../../hooks/useLinkedAccounts";

interface Trading212ApiKeyManagerProps {
  broker: BrokerProvider;
  hasExistingAccount?: boolean;
  existingAccount?: LinkedAccount;
  onSuccess?: (data: any) => void;
  hideNavigation?: boolean;
  embedded?: boolean;
}

const Trading212ApiKeyManager = ({ 
  broker, 
  hasExistingAccount = false, 
  existingAccount,
  onSuccess,
  hideNavigation = false,
  embedded = false
}: Trading212ApiKeyManagerProps) => {
  const [newApiKey, setNewApiKey] = useState("");
  const [accountNickname, setAccountNickname] = useState("");
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const [showSuccessSnackbar, setShowSuccessSnackbar] = useState(false);
  const [showErrorSnackbar, setShowErrorSnackbar] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [lastAction, setLastAction] = useState<'add' | 'delete' | 'sync' | null>(null);

  const linkAccountMutation = useLinkAccount();
  const deleteAccountMutation = useDeleteAccount();
  const triggerSyncMutation = useTriggerSync();

  const isLoading = linkAccountMutation.isPending || deleteAccountMutation.isPending;

  const handleAddApiKey = async () => {
    if (!newApiKey.trim()) return;

    const nickname = accountNickname.trim() || `${broker.displayName} Account`;
    
    try {
      const result = await linkAccountMutation.mutateAsync({
        provider: broker.id,
        api_key: newApiKey.trim(),
        account_nickname: nickname,
      });

      setNewApiKey("");
      setAccountNickname("");
      setLastAction('add');
      setShowSuccessSnackbar(true);
      
      // Call onSuccess if provided (for wizard flow)
      if (onSuccess) {
        onSuccess({
          apiKey: newApiKey.trim(),
          accountNickname: nickname,
          accountId: result?.account?.id,
        });
      }
    } catch (error: any) {
      setErrorMessage(error.error || "Failed to connect account. Please check your API key and try again.");
      setShowErrorSnackbar(true);
    }
  };

  const handleDeleteAccount = async () => {
    if (!existingAccount) return;
    
    try {
      await deleteAccountMutation.mutateAsync(existingAccount.id);
      setShowDeleteDialog(false);
      setLastAction('delete');
      setShowSuccessSnackbar(true);
    } catch (error: any) {
      setErrorMessage(error.error || "Failed to delete account connection.");
      setShowErrorSnackbar(true);
    }
  };

  const handleTriggerSync = async () => {
    if (!existingAccount) return;
    
    try {
      await triggerSyncMutation.mutateAsync({ 
        accountId: existingAccount.id 
      });
      setLastAction('sync');
      setShowSuccessSnackbar(true);
    } catch (error: any) {
      setErrorMessage(error.error || "Failed to trigger sync.");
      setShowErrorSnackbar(true);
    }
  };

  const validateApiKey = (key: string) => {
    // Basic validation - Trading212 API keys are typically UUIDs
    const trimmedKey = key.trim();
    if (trimmedKey.length < 20) return "API key appears too short";
    if (!/^[a-zA-Z0-9\-_]+$/.test(trimmedKey)) return "API key contains invalid characters";
    return null;
  };

  const apiKeyError = newApiKey ? validateApiKey(newApiKey) : null;

  return (
    <Box>
      {/* Existing Account */}
      {hasExistingAccount && existingAccount && (
        <Card sx={{ mb: 3 }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <KeyIcon sx={{ mr: 2, color: "success.main" }} />
                <Box>
                  <Typography variant="h6">
                    Connected Account
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {existingAccount.account_nickname}
                  </Typography>
                </Box>
              </Box>
              <Chip
                icon={<CheckIcon />}
                label={existingAccount.account_status === 'active' ? 'Active' : existingAccount.account_status}
                color={existingAccount.account_status === 'active' ? 'success' : 'warning'}
                variant="filled"
              />
            </Box>

            {/* Masked API Key Display */}
            <TextField
              fullWidth
              label="API Key"
              value="••••••••••••••••••••••••••••••••••••••••"
              InputProps={{
                readOnly: true,
                startAdornment: <SecurityIcon sx={{ mr: 1, color: "text.secondary" }} />,
              }}
              helperText="Your API key is securely stored"
              sx={{ mb: 2 }}
            />

            {/* Account Actions */}
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              <Button
                variant="outlined"
                onClick={handleTriggerSync}
                disabled={triggerSyncMutation.isPending}
                startIcon={triggerSyncMutation.isPending ? 
                  <CircularProgress size={16} /> : 
                  <CheckIcon />
                }
              >
                {triggerSyncMutation.isPending ? "Syncing..." : "Sync Now"}
              </Button>
              
              <Button
                variant="outlined"
                color="error"
                onClick={() => setShowDeleteDialog(true)}
                disabled={isLoading}
                startIcon={<DeleteIcon />}
              >
                Disconnect
              </Button>
            </Box>

            {/* Sync Status */}
            {existingAccount.last_sync_at && (
              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  Last sync: {new Date(existingAccount.last_sync_at).toLocaleString()}
                  {existingAccount.last_sync_status === 'error' && (
                    <> • <strong>Sync failed</strong> - please check your API key</>
                  )}
                </Typography>
              </Alert>
            )}
          </CardContent>
        </Card>
      )}

      {/* Add New Account */}
      {!hasExistingAccount && (
        <Card>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
              <AddIcon sx={{ mr: 2, color: "primary.main" }} />
              <Typography variant="h6">
                Add Your API Key
              </Typography>
            </Box>

            {/* API Key Input */}
            <TextField
              fullWidth
              label="Trading212 API Key"
              value={newApiKey}
              onChange={(e) => setNewApiKey(e.target.value)}
              placeholder="Enter your Trading212 API key"
              error={!!apiKeyError}
              helperText={apiKeyError || "Your API key will be securely stored"}
              InputProps={{
                startAdornment: <KeyIcon sx={{ mr: 1, color: "text.secondary" }} />,
              }}
              sx={{ mb: 2 }}
            />

            {/* Account Nickname */}
            <TextField
              fullWidth
              label="Account Nickname (Optional)"
              value={accountNickname}
              onChange={(e) => setAccountNickname(e.target.value)}
              placeholder="e.g., My ISA, Main Trading Account"
              helperText="Give your account a memorable name"
              sx={{ mb: 3 }}
            />

            {/* Connect Button */}
            <Button
              variant="contained"
              size="large"
              fullWidth
              onClick={handleAddApiKey}
              disabled={!newApiKey.trim() || !!apiKeyError || isLoading}
              startIcon={isLoading ? 
                <CircularProgress size={20} color="inherit" /> : 
                <CheckIcon />
              }
              sx={{ mb: 2 }}
            >
              {isLoading ? "Connecting..." : "Connect Account"}
            </Button>

            {/* Setup Instructions */}
            <Button
              variant="text"
              fullWidth
              onClick={() => setShowInstructions(!showInstructions)}
              endIcon={showInstructions ? <ExpandLess /> : <ExpandMore />}
            >
              How to get your API key
            </Button>

            <Collapse in={showInstructions}>
              <Box sx={{ 
                mt: 2, 
                p: 3, 
                backgroundColor: theme => theme.palette.mode === 'dark'
                  ? 'rgba(255, 255, 255, 0.02)'
                  : 'rgba(0, 0, 0, 0.02)',
                border: theme => `1px solid ${theme.palette.divider}`,
                borderRadius: 2 
              }}>
                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                  Getting your Trading212 API key:
                </Typography>
                <Box component="ol" sx={{ m: 0, p: 0, pl: 2, listStyle: 'none' }}>
                  {[
                    'Log into your Trading212 account',
                    'Navigate to Settings → API',
                    'Generate a new API key with read-only permissions',
                    'Copy the API key and paste it above'
                  ].map((step, index) => (
                    <Box 
                      key={index}
                      component="li" 
                      sx={{ 
                        display: 'flex', 
                        alignItems: 'flex-start',
                        gap: 2,
                        mb: 1.5,
                        '&:last-child': { mb: 0 }
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 24,
                          height: 24,
                          borderRadius: '50%',
                          backgroundColor: 'primary.main',
                          color: 'primary.contrastText',
                          fontSize: '0.875rem',
                          fontWeight: 600,
                          flexShrink: 0,
                          mt: 0.125,
                        }}
                      >
                        {index + 1}
                      </Box>
                      <Typography variant="body2" sx={{ lineHeight: 1.5 }}>
                        {step}
                      </Typography>
                    </Box>
                  ))}
                </Box>

                <Alert severity="warning" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    <strong>Important:</strong> Only grant read-only permissions. We never need 
                    write access to execute trades or modify your account.
                  </Typography>
                </Alert>
              </Box>
            </Collapse>
          </CardContent>
        </Card>
      )}

      {/* Security Information */}
      <Alert severity="info" sx={{ mt: 3 }} icon={<SecurityIcon />}>
        <Typography variant="body2">
          <strong>Security Promise:</strong> Your API key is stored securely with encryption at rest. 
          We can only read your portfolio data and cannot execute trades or access your funds.
        </Typography>
      </Alert>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onClose={() => setShowDeleteDialog(false)}>
        <DialogTitle>
          Disconnect {broker.displayName} Account?
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            This will remove your {broker.displayName} connection and stop automatic portfolio updates.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Your existing portfolio data will be preserved, but no new transactions will be imported.
            You can reconnect at any time.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteDialog(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteAccount}
            color="error"
            disabled={deleteAccountMutation.isPending}
            startIcon={deleteAccountMutation.isPending ? 
              <CircularProgress size={16} /> : 
              <DeleteIcon />
            }
          >
            {deleteAccountMutation.isPending ? "Disconnecting..." : "Disconnect"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={showSuccessSnackbar}
        autoHideDuration={6000}
        onClose={() => setShowSuccessSnackbar(false)}
      >
        <Alert 
          onClose={() => setShowSuccessSnackbar(false)} 
          severity="success"
          sx={{ width: "100%" }}
        >
          {lastAction === 'add' 
            ? "Account connected successfully! Your data is being synchronized."
            : lastAction === 'delete'
            ? "Account disconnected successfully."
            : lastAction === 'sync'
            ? "Portfolio sync started successfully."
            : "Action completed successfully!"
          }
        </Alert>
      </Snackbar>

      {/* Error Snackbar */}
      <Snackbar
        open={showErrorSnackbar}
        autoHideDuration={8000}
        onClose={() => setShowErrorSnackbar(false)}
      >
        <Alert 
          onClose={() => setShowErrorSnackbar(false)} 
          severity="error"
          sx={{ width: "100%" }}
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Trading212ApiKeyManager;